import { useMount } from 'ahooks';
import { useSetAtom } from 'jotai';
import { observer } from 'mobx-react-lite';
import { lazy, Suspense } from 'react';
import { Navigate, useNavigate, useRoutes } from 'react-router-dom';

import { implog } from '@/api/implog';
import { ACCESS_TOKEN_KEY, ACCESS_USERID_KEY } from '@/constant/config';
import { useDailyModalStore } from '@/jotai/daygold';
import { asyncEmitterAtom } from '@/jotai/emitter';
import { asyncCollectGamesAtom, asyncGamesAtom } from '@/jotai/game';
import { initDayFirstLogin, userStateAtom } from '@/jotai/user';
import AmountMenu from '@/modules/AmountMenu';
import { history } from '@/routers/history';
import { modalStore } from '@/stores/modal';
import globalStore from '@/stores/store';
import Storage from '@/utils/Storage';
import { getUniqueKey, getUrlParam, inPwaEnv, isUserQuit } from '@/utils/utils';

const Layout = lazy(() => import('@/Layout'));
const LayoutSuspense = lazy(() => import('@/Layout/suspense'));
const Game = lazy(() => import('@/pages/game'));
const Login = lazy(() => import('@/pages/login'));
const My = lazy(() => import('@/pages/my'));
const Recharge = lazy(() => import('@/pages/recharge/recharge'));
const RechargeRecord = lazy(() => import('@/pages/recharge/record'));
const JackpotWithdraw = lazy(() => import('@/modules/jackpot/withdraw/index'));
const JackpotWithdrawRecord = lazy(() => import('@/modules/jackpot/record'));
const Promotion = lazy(() => import('@/pages/promotion'));
const Whatsapp = lazy(() => import('@/pages/whatsapp'));
const WhatsappWithdrawRecord = lazy(() => import('@/pages/whatsapp/record'));
const WhatsappWithdraw = lazy(() => import('@/pages/whatsapp/withdraw'));
const BonusIndex = lazy(() => import('@/pages/bonus/bonus'));
const Pay = lazy(() => import('@/pages/recharge/pay'));
const Payment = lazy(() => import('@/pages/recharge/pay/payment'));
const Withdraw = lazy(() => import('@/pages/withdraw/withdraw'));
const WithdrawRecord = lazy(() => import('@/pages/withdraw/record'));
const WithdrawFreeRecord = lazy(() => import('@/pages/withdraw/record-free'));
// const WithdrawFreeTaskV3 = lazy(() => import('@/pages/withdraw/free/task_v3'));
const Play = lazy(() => import('@/pages/play'));
const VipNew = lazy(() => import('@/pages/vip/new'));
const PlayHelp = lazy(() => import('@/pages/play/help'));
const TemaConditions = lazy(() => import('@/pages/temaconditions'));
const RechargeChoose = lazy(() => import('@/pages/recharge/choose'));
// const WithdrawFreeTask = lazy(() => import('@/pages/withdraw_task/withdraw'));
const WithdrawFree = lazy(() => import('@/pages/withdraw/free'));
const WithdrawFreeNew = lazy(() => import('@/pages/withdraw/free/new'));
const WithdrawFreeNewDetail = lazy(() => import('@/pages/withdraw/free/new/detail'));
const Mail = lazy(() => import('@/pages/mail'));
const Advertise = lazy(() => import('@/pages/advertise/v2/index'));
const FirstRecharge = lazy(() => import('@/pages/first-charge'));
const FirstRechargeNotRecharged = lazy(() => import('@/pages/first-charge/not-recharged'));
const FirstRechargeRecharged = lazy(() => import('@/pages/first-charge/recharged'));
const ContinueRecharge = lazy(() => import('@/pages/continue-recharge'));

export default observer(function Router() {
  const getGames = useSetAtom(asyncGamesAtom);
  const getCollectGames = useSetAtom(asyncCollectGamesAtom);
  const token = Storage.getAccess(ACCESS_TOKEN_KEY);
  const userid = Storage.getAccess(ACCESS_USERID_KEY);
  const navigate = useNavigate();
  const setUserState = useSetAtom(userStateAtom);
  const setEmitter = useSetAtom(asyncEmitterAtom);
  // const emitter = useAtomValue(emitterAtom);
  const setDayFirstLogin = useSetAtom(initDayFirstLogin);
  const [getDaygold] = useDailyModalStore();
  // const { pathname } = useLocation();
  useMount(async () => {
    history.push = navigate;
    const cookie = document.cookie;
    const cookieArr = cookie.split(';');
    const cookieObj = {};
    cookieArr.forEach(item => {
      const [k, v] = item.split('=');
      const trimmedKey = k ? k.trim() : '';
      const trimmedValue = v ? v.trim() : '';
      if (trimmedKey && trimmedValue) cookieObj[trimmedKey] = trimmedValue;
    });
    const ORIGINAL_DOMAIN = cookieObj['ORIGINAL_DOMAIN'];
    if (!inPwaEnv()) {
      /**
       * 如果不是PWA环境，同时缓存中有ORIGINAL_DOMAIN，并且ORIGINAL_DOMAIN和当前域名不一致，则跳转到ORIGINAL_DOMAIN
       */
      if (ORIGINAL_DOMAIN && ORIGINAL_DOMAIN !== window.location.origin) {
        window.location.href = window.location.href.replace(window.location.origin, ORIGINAL_DOMAIN);
        return;
      }
    }
    setEmitter();
    if (!token && !isUserQuit()) {
      const res = await getUniqueKey();
      await globalStore.touristsLogin(res);
    }
    setDayFirstLogin();
    getGames();
    getCollectGames();
    await globalStore.initConfig();
    setUserState(globalStore.initialState); // 更新jotai用户信息
    if (!globalStore?.initialState?.isLogin && isUserQuit()) {
      modalStore.openNotLoginWelcome();
    } else if (globalStore?.initialState?.isLogin) {
      await getDaygold();
      // emitter.emit({ type: 'withdraw_task_update' });
    }
    (window as any).instanceKey = Math.random().toString(36).substr(2, 9);
    (window as any).lqipmap = await fetch(
      `${window.imgPrefix}/progressive/lqip-map.json?v=${(window as any).instanceKey}`
    ).then(res => res.json());

    // 落地页曝光人数
    /** 召回和注册共用push_callback
     * push_callback未变化则触发后续操作
     * 注册是push_callback_register
     * 召回回是push_callback_recall
     */
    const call_kind = getUrlParam('call_kind');
    const call_userid = getUrlParam('call_userid');
    const push_callback = getUrlParam('push_callback');
    if (call_kind && push_callback) {
      const recall_push_callback = Storage.get(call_kind);
      if (recall_push_callback !== push_callback) {
        Storage.set(call_kind, push_callback);
        implog('pageview', `${call_kind}|${push_callback}`, call_userid || userid, 'zh');

        switch (call_kind) {
          case 'recall_User_Wage':
            navigate('/bonus/index');
            modalStore.openDaily({ from: 'bonus' });
            break;
          case 'recall_User_Alms':
            navigate('/bonus/index');
            modalStore.openDole();
            break;
          case 'recall_User_BetFlow':
            navigate('/mail');
            break;
          default:
            break;
        }
      }
    }
    const register_push_callback = Storage.get('push_callback');
    if (!call_kind && push_callback && push_callback !== register_push_callback) {
      Storage.set('push_callback', push_callback);
      Storage.remove('push_callback_register');
      // 删除url中的push_callback 并不跳转
      const url = window.location.href;
      const new_url = url.replace(/([&?])push_callback=[^&]*(?:&|$)/, (match, p1) => (p1 === '?' ? p1 : ''));

      window.history.replaceState({}, '0', new_url);

      implog('push_hall_show', '', userid);
    }
  });

  const routes = useRoutes([
    {
      element: <Layout />,
      path: '/',
      children: [
        { element: <Navigate to={'/game'} />, index: true },
        {
          path: '/first-recharge',
          element: <FirstRecharge />,
          children: [
            {
              path: 'not-recharged',
              element: <FirstRechargeNotRecharged />
            },
            { path: 'recharged', element: <FirstRechargeRecharged /> }
          ]
        },
        {
          path: '/continue-recharge',
          element: <ContinueRecharge />
        },
        {
          element: <AmountMenu />,
          children: [
            {
              path: '/recharge',
              element: <Recharge />
            },
            {
              path: '/recharge/record',
              element: <RechargeRecord />
            },
            {
              path: '/withdraw',
              element: <Withdraw />
            },
            {
              path: '/withdraw/record',
              element: <WithdrawRecord />
            },
            {
              path: '/vip',
              element: <VipNew />
            }
          ]
        },
        {
          path: '/recharge/choose/:args',
          element: <RechargeChoose />
        },
        {
          path: '/mail',
          element: <Mail />
        },
        {
          path: '/advertise',
          element: <Advertise />
        },
        {
          path: '/withdraw/free/record',
          element: <WithdrawFreeRecord />
        },
        {
          path: '/withdraw/free',
          element: <WithdrawFree />
        },
        {
          path: '/withdraw/free/new',
          element: <WithdrawFreeNew />
        },
        {
          path: '/withdraw/free/new/detail',
          element: <WithdrawFreeNewDetail />
        },
        {
          path: '/withdraw/free/task',
          element: <WithdrawFree />
        },
        {
          path: '/promotion',
          element: <Promotion />
        },

        {
          path: '/bonus/index',
          element: <BonusIndex />
          // children: [
          //   {
          //     path: 'index',
          //     element: <BonusIndex />
          //   },
          //   {
          //     path: 'tarefa',
          //     element: <BonusTarefa />
          //   },
          //   {
          //     path: 'vip',
          //     element: <BonusVip />
          //   },
          //   { path: 'ranking', element: <Ranking /> }
          // ]
        },
        {
          path: '/game',
          element: <Game />
        },
        {
          path: '/play',
          element: <Play />
        },
        {
          path: '/game/help',
          element: <PlayHelp />
        },
        {
          path: '/share',
          element: <Whatsapp />
        },
        {
          path: '/whatsapp/withdraw/record',
          element: <WhatsappWithdrawRecord />
        },
        {
          path: '/whatsapp/withdraw',
          element: <WhatsappWithdraw />
        },
        {
          path: '/jackpot/withdraw/record',
          element: <JackpotWithdrawRecord />
        },
        {
          path: '/jackpot/withdraw',
          element: <JackpotWithdraw />
        },
        {
          path: '/my',
          element: <My />
        },
        {
          path: '/pay/:path',
          element: <Pay />
        },
        {
          path: '/payment/:path',
          element: <Payment />
        },
        {
          path: '/tema_conditions',
          element: <TemaConditions />
        },
        {
          path: '/login',
          element: <Login />
        }
      ]
    }
  ]);

  // if (globalStore.initialState.isLoading) {
  //   return <Loading loading={true} />;
  // }

  return <Suspense fallback={<LayoutSuspense />}>{routes}</Suspense>;
});
